import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Dices } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';

const DICE_TYPES = [
  { sides: 4, color: 'from-blue-500 to-blue-600' },
  { sides: 6, color: 'from-green-500 to-green-600' },
  { sides: 8, color: 'from-purple-500 to-purple-600' },
  { sides: 10, color: 'from-orange-500 to-orange-600' },
  { sides: 12, color: 'from-pink-500 to-pink-600' },
  { sides: 20, color: 'from-red-500 to-red-600' },
  { sides: 100, color: 'from-indigo-500 to-indigo-600' },
];

export default function DiceRoller({ isOpen, onClose }) {
  const [customRoll, setCustomRoll] = useState('');
  const [rollHistory, setRollHistory] = useState([]);

  const parseDiceNotation = (notation) => {
    // Parse dice notation like "2d6+3", "1d20", "3d8-2"
    const regex = /^(\d+)?d(\d+)([+-]\d+)?$/i;
    const match = notation.trim().match(regex);
    
    if (!match) return null;
    
    const count = parseInt(match[1] || '1');
    const sides = parseInt(match[2]);
    const modifier = match[3] ? parseInt(match[3]) : 0;
    
    return { count, sides, modifier };
  };

  const rollDice = (count, sides, modifier = 0, label = null) => {
    const rolls = [];
    for (let i = 0; i < count; i++) {
      rolls.push(Math.floor(Math.random() * sides) + 1);
    }
    
    const sum = rolls.reduce((a, b) => a + b, 0);
    const total = sum + modifier;
    
    const rollLabel = label || `${count}d${sides}${modifier !== 0 ? (modifier > 0 ? `+${modifier}` : modifier) : ''}`;
    
    const newRoll = {
      id: Date.now(),
      label: rollLabel,
      rolls,
      modifier,
      total,
      timestamp: new Date().toLocaleTimeString(),
    };
    
    setRollHistory(prev => [newRoll, ...prev.slice(0, 9)]);
  };

  const handleQuickRoll = (sides) => {
    rollDice(1, sides, 0, `d${sides}`);
  };

  const handleCustomRoll = () => {
    const parsed = parseDiceNotation(customRoll);
    if (parsed) {
      rollDice(parsed.count, parsed.sides, parsed.modifier);
      setCustomRoll('');
    }
  };

  const clearHistory = () => {
    setRollHistory([]);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-[color:var(--overlay-scrim)] backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-bg-modal rounded-t-3xl md:rounded-2xl w-full md:max-w-2xl max-h-[75vh] md:max-h-[90vh] overflow-hidden shadow-overlay border border-modal"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-burgundy to-burgundy-light text-white p-4 md:p-6 flex items-center justify-between sticky top-0 z-10">
            <div className="flex items-center gap-2 md:gap-3">
              <Dices className="w-5 h-5 md:w-6 md:h-6" />
              <h2 className="text-xl md:text-2xl font-bold section-heading">
                Dice Roller
              </h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20 h-8 w-8 md:h-10 md:w-10"
            >
              <X className="w-5 h-5 md:w-6 md:h-6" />
            </Button>
          </div>

          <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(75vh-64px)] md:max-h-[calc(90vh-80px)]">
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              {rollHistory[0]
                ? `${rollHistory[0].label}, total ${rollHistory[0].total}`
                : ''}
            </div>
            {/* Quick Roll Buttons */}
            <div className="mb-4 md:mb-6">
              <h3 className="text-xs md:text-sm font-semibold text-text-muted mb-2 md:mb-3 uppercase tracking-wide">
                Quick Roll
              </h3>
              <div className="grid grid-cols-4 gap-2 md:gap-3">
                {DICE_TYPES.map((dice) => (
                  <motion.button
                    key={dice.sides}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleQuickRoll(dice.sides)}
                    className={`bg-gradient-to-br ${dice.color} text-white rounded-lg md:rounded-xl p-3 md:p-4 font-bold text-base md:text-lg shadow-lg hover:shadow-xl transition-shadow`}
                  >
                    d{dice.sides}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Custom Roll Input */}
            <div className="mb-4 md:mb-6">
              <h3 className="text-xs md:text-sm font-semibold text-text-muted mb-2 md:mb-3 uppercase tracking-wide">
                Custom Roll
              </h3>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="e.g., 2d6+3, 1d20, 3d8-2"
                  value={customRoll}
                  onChange={(e) => setCustomRoll(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleCustomRoll()}
                  className="flex-1 border-2 border-forest-green/30 focus:border-forest-green"
                />
                <Button
                  onClick={handleCustomRoll}
                  className="bg-forest-green hover:bg-forest-green-light text-white"
                  disabled={!customRoll}
                >
                  <Dices className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-xs text-text-muted mt-2">
                Format: [count]d[sides][+/-modifier] (e.g., 2d6+3)
              </p>
            </div>

            {/* Roll History */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wide">
                  Roll History
                </h3>
                {rollHistory.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearHistory}
                    className="text-xs text-burgundy hover:text-burgundy-light"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>

              {rollHistory.length === 0 ? (
                <Card className="p-8 text-center border-2 border-dashed border-text-muted/30">
                  <Dices className="w-12 h-12 mx-auto mb-2 text-text-muted/50" />
                  <p className="text-text-muted">No rolls yet. Roll some dice!</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  <AnimatePresence>
                    {rollHistory.map((roll) => (
                      <motion.div
                        key={roll.id}
                        initial={{ opacity: 0, y: -20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="content-container rounded-xl p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-forest-green section-heading">
                              {roll.label}
                            </span>
                            <span className="text-xs text-text-muted">{roll.timestamp}</span>
                          </div>
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring' }}
                            className="text-3xl font-bold bg-gradient-to-br from-burgundy to-burgundy-light bg-clip-text text-transparent section-heading"
                          >
                            {roll.total}
                          </motion.div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {roll.rolls.map((die, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ scale: 0, rotate: -180 }}
                              animate={{ scale: 1, rotate: 0 }}
                              transition={{ delay: idx * 0.05 }}
                              className="w-8 h-8 bg-forest-green/10 rounded border border-forest-green/30 flex items-center justify-center text-sm font-semibold text-forest-green"
                            >
                              {die}
                            </motion.div>
                          ))}
                          {roll.modifier !== 0 && (
                            <div className="flex items-center text-sm font-semibold text-text-muted">
                              <Plus className="w-4 h-4 mx-1" />
                              <span>{roll.modifier > 0 ? `+${roll.modifier}` : roll.modifier}</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}